import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import inventoryApi from '../../services/inventoryApi';
import ingredientApi from '../../services/ingredientApi';
import PageLoader from '../common/PageLoader';
import InventoryCategory from '../inventory/InventoryCategory';
import IngredientList from '../inventory/IngredientList';
import FinishedGoodsTab from '../inventory/FinishedGoodsTab';
import PageHeader from '../common/PageHeader';
import AddIngredientModal from '../modals/AddIngredientModal';
import productionApi from '../../services/productionApi';
import { formatQuantity } from '../../utils/formatters';
import './InventoryManager.css';

const ALL_STICKY_CATEGORIES = [
    { id: 'all', label: 'All Categories', icon: 'all' },
    { id: 'Coffee', label: 'Coffee', icon: 'coffee' },
    { id: 'Tea', label: 'Tea', icon: 'tea' },
    { id: 'Juice Concentrates', label: 'Juice Concentrates', icon: 'juice' },
    { id: 'Botanicals', label: 'Botanicals', icon: 'botanicals' },
    { id: 'Sweeteners', label: 'Sweeteners', icon: 'sweeteners' },
    { id: 'Packaging', label: 'Packaging', icon: 'packaging' },
    { id: 'Others', label: 'Others', icon: 'others' }
];

const getStockStatus = (ingredient) => {
    if (!ingredient.current_stock && ingredient.current_stock !== 0) {
        return 'normal';
    }
    if (ingredient.current_stock === 0) {
        return 'out-of-stock';
    }
    if (ingredient.current_stock <= (ingredient.reorder_point || 0)) {
        return 'low-stock';
    }
    return 'normal';
};

/**
 * InventoryManager Component
 * Displays and manages inventory items in a responsive grid layout
 * Now supports dual view: Inventory Levels and Ingredient Details
 */
const InventoryManager = () => {
    const { userType } = useAuth();
    const { showToast } = useToast();

    // UI State
    const [activeTab, setActiveTab] = useState('levels'); // 'levels' or 'details'
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isSticky, setIsSticky] = useState(false);
    const [error, setError] = useState(null);
    const [isAddIngredientModalOpen, setIsAddIngredientModalOpen] = useState(false);
    const [selectedIngredient, setSelectedIngredient] = useState(null); // For edit mode

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [stockFilter, setStockFilter] = useState('all');
    const [sortBy, setSortBy] = useState('name');

    // Data State
    const [inventory, setInventory] = useState({});
    const [originalInventory, setOriginalInventory] = useState({});
    const [ingredients, setIngredients] = useState([]);
    const [finishedGoods, setFinishedGoods] = useState([]);
    const [isFinishedGoodsLoading, setIsFinishedGoodsLoading] = useState(false);

    // Check if user is admin or super admin
    const isAdmin = userType === 'ADMIN' || userType === 'SUPER_ADMIN';
    const [filters] = useState({
        skip: 0,
        limit: 1000,
        low_stock_only: false,
        reorder_only: false
    });

    // Fetch inventory data (Levels Tab)
    const fetchInventory = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await inventoryApi.list(filters);
            const data = Array.isArray(response) ? response : (response.items || []);

            // Category display name mapping
            const categoryDisplayNames = {
                'COFFEE': 'Coffee',
                'TEA': 'Tea',
                'JUICE': 'Juice Concentrates',
                'FLAVORING': 'Botanicals',
                'ADDITIVES': 'Botanicals',
                'SWEETENER': 'Sweeteners',
                'SYRUP': 'Sweeteners',
                'PACKAGING': 'Packaging',
                'OTHER': 'Others',
                'other': 'Others'
            };

            // Group items by category with display names
            const groupedInventory = {};
            data.forEach(item => {
                const categoryKey = item.ingredient_category || 'Uncategorized';
                // Use display name if available, otherwise use the raw category
                const categoryName = categoryDisplayNames[categoryKey] || categoryKey;

                if (!groupedInventory[categoryName]) {
                    groupedInventory[categoryName] = [];
                }
                // Keep the original item structure - InventoryItem component uses these field names
                groupedInventory[categoryName].push(item);
            });

            setInventory(groupedInventory);
            setOriginalInventory(JSON.parse(JSON.stringify(groupedInventory))); // Deep copy
        } catch (err) {
            console.error('Error fetching inventory:', err);
            setError('Failed to load inventory data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [filters]);

    // Fetch ingredients data (Details Tab) — merged with inventory levels for current_stock
    const fetchIngredients = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [ingredientsResponse, inventoryResponse] = await Promise.all([
                ingredientApi.list({ limit: 1000 }),
                inventoryApi.list({ skip: 0, limit: 1000 }),
            ]);

            const ingredientItems = Array.isArray(ingredientsResponse)
                ? ingredientsResponse
                : (ingredientsResponse.items || []);

            // Build ingredient_id → current_quantity lookup from inventory levels
            const stockMap = {};
            (inventoryResponse?.items || []).forEach(inv => {
                stockMap[inv.ingredient_id] = inv.current_quantity;
            });

            // Attach current_stock so IngredientList status/KPI logic works correctly
            const enriched = ingredientItems.map(ing => ({
                ...ing,
                current_stock: stockMap[ing.id] ?? 0,
            }));

            setIngredients(enriched);
        } catch (err) {
            console.error('Error fetching ingredients:', err);
            setError('Failed to load ingredients data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchFinishedGoods = useCallback(async () => {
        setIsFinishedGoodsLoading(true);
        try {
            const data = await productionApi.listFinishedGoods();
            setFinishedGoods(data);
        } catch (err) {
            console.error('Error fetching finished goods for stats:', err);
        } finally {
            setIsFinishedGoodsLoading(false);
        }
    }, []);

    // Keep the filter toolbar visually pinned once the page has scrolled past it
    useEffect(() => {
        if (activeTab !== 'levels') return;

        const handleScroll = () => {
            setIsSticky(window.scrollY >= 120);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        return () => window.removeEventListener('scroll', handleScroll);
    }, [activeTab]);

    // Category chips are real tabs: selecting one shows only that category
    const handleCategoryChipClick = useCallback((categoryName) => {
        setCategoryFilter(categoryName);
    }, []);

    // Initial data load based on active tab
    useEffect(() => {
        if (activeTab === 'levels') {
            fetchInventory();
        } else if (activeTab === 'details') {
            fetchIngredients();
        } else if (activeTab === 'finished-goods') {
            fetchFinishedGoods();
        }
    }, [activeTab, fetchInventory, fetchIngredients, fetchFinishedGoods]);

    // Handle quantity change
    const handleQuantityChange = useCallback((categoryName, itemId, newQuantity, newUnit) => {
        setInventory(prevInventory => {
            const updatedInventory = { ...prevInventory };
            const categoryItems = [...updatedInventory[categoryName]];
            const itemIndex = categoryItems.findIndex(item => item.id === itemId);

            if (itemIndex !== -1) {
                categoryItems[itemIndex] = {
                    ...categoryItems[itemIndex],
                    current_quantity: newQuantity // Use backend field name
                };
                updatedInventory[categoryName] = categoryItems;
            }

            return updatedInventory;
        });
    }, []);

    // Handle unit of measure change
    const handleUnitChange = useCallback((categoryName, itemId, newUnit) => {
        setInventory(prevInventory => {
            const updatedInventory = { ...prevInventory };
            const categoryItems = [...updatedInventory[categoryName]];
            const itemIndex = categoryItems.findIndex(item => item.id === itemId);
            if (itemIndex !== -1) {
                categoryItems[itemIndex] = {
                    ...categoryItems[itemIndex],
                    ingredient_unit: newUnit, // Update the unit field
                };
                updatedInventory[categoryName] = categoryItems;
            }
            return updatedInventory;
        });
    }, []);

    // Handle needed/shortage change (for admin users)
    const handleNeededChange = useCallback((categoryName, itemId, field, value) => {
        if (!isAdmin) return;

        setInventory(prevInventory => {
            const updatedInventory = { ...prevInventory };
            const categoryItems = [...updatedInventory[categoryName]];
            const itemIndex = categoryItems.findIndex(item => item.id === itemId);

            if (itemIndex !== -1) {
                categoryItems[itemIndex] = {
                    ...categoryItems[itemIndex],
                    [field]: value
                };
                updatedInventory[categoryName] = categoryItems;
            }

            return updatedInventory;
        });
    }, [isAdmin]);

    // Check if there are unsaved changes
    const hasChanges = useCallback(() => {
        // Only applicable for 'levels' tab
        if (activeTab !== 'levels') return false;

        // Compare current inventory with original
        for (const category in inventory) {
            const currentItems = inventory[category];
            const originalItems = originalInventory[category] || [];

            for (const currentItem of currentItems) {
                const originalItem = originalItems.find(item => item.id === currentItem.id);

                if (!originalItem) return true;

                // Check for quantity change
                if (currentItem.current_quantity !== originalItem.current_quantity) return true;

                // Check for unit change
                if (currentItem.ingredient_unit !== originalItem.ingredient_unit) return true;

                if (isAdmin) {
                    if (currentItem.needed !== originalItem.needed) return true;
                    if (currentItem.shortage !== originalItem.shortage) return true;
                }
            }
        }

        return false;
    }, [inventory, originalInventory, isAdmin, activeTab]);

    // Save inventory changes
    const handleSave = async () => {
        if (!hasChanges()) return;

        setIsSaving(true);

        try {
            const changedItems = {};

            for (const category in inventory) {
                const currentItems = inventory[category];
                const originalItems = originalInventory[category] || [];

                for (const currentItem of currentItems) {
                    const originalItem = originalItems.find(item => item.id === currentItem.id);

                    const hasStockChanged =
                        !originalItem || currentItem.current_quantity !== originalItem.current_quantity;

                    // Check for unit change
                    const hasUnitChanged = currentItem.ingredient_unit !== originalItem.ingredient_unit;

                    const hasAdminChanged =
                        isAdmin && (
                            currentItem.needed !== originalItem.needed ||
                            currentItem.shortage !== originalItem.shortage
                        );

                    if (hasStockChanged || hasAdminChanged || hasUnitChanged) {

                        const update = {
                            quantity: currentItem.current_quantity,
                            type: currentItem.updateType ?? "set" // set / add / subtract
                        };

                        if (hasUnitChanged) {
                            update.unit = currentItem.ingredient_unit;
                            update.ingredient_id = currentItem.ingredient_id;
                        }

                        if (isAdmin) {
                            if (currentItem.needed !== originalItem.needed) {
                                update.needed = currentItem.needed;
                            }

                            if (currentItem.shortage !== originalItem.shortage) {
                                update.shortage = currentItem.shortage;
                            }
                        }

                        changedItems[currentItem.id] = update;
                    }
                }
            }

            if (Object.keys(changedItems).length > 0) {
                const requestBody = {
                    updates: changedItems
                };
                await inventoryApi.bulkUpdate(requestBody);

                setOriginalInventory(JSON.parse(JSON.stringify(inventory)));
                showToast("Inventory updated successfully", "success");
            }

        } catch (err) {
            console.error("Error saving inventory:", err);
            showToast("Failed to update inventory. Please try again.", "error");
        } finally {
            setIsSaving(false);
        }
    };

    // Reset changes
    const handleReset = () => {
        setInventory(JSON.parse(JSON.stringify(originalInventory)));
        showToast('Changes discarded', 'info');
    };

    // Handler for editing an ingredient
    const handleEditIngredient = (ingredient) => {
        setSelectedIngredient(ingredient);
        setIsAddIngredientModalOpen(true);
    };

    // Handler for deleting an ingredient
    const handleDeleteIngredient = async (ingredient) => {
        try {
            await ingredientApi.remove(ingredient.id);
            showToast(`Ingredient ${ingredient.name} deleted successfully`, 'success');
            // Refresh list
            fetchIngredients();
        } catch (err) {
            console.error('Error deleting ingredient:', err);
            showToast(`Failed to delete ingredient: ${err.message}`, 'error');
        }
    };

    // Handler when modal closes
    const handleModalClose = () => {
        setIsAddIngredientModalOpen(false);
        setSelectedIngredient(null);
    };

    // Handler when ingredient is created/updated
    const handleIngredientSaved = () => {
        // Always refresh both views so neither tab shows stale data
        fetchInventory();
        fetchIngredients();
        showToast(`Ingredient ${selectedIngredient ? 'updated' : 'created'} successfully`, 'success');
    };

    // Calculate KPI metrics from inventory data
    const kpiMetrics = useMemo(() => {
        const allItems = Object.values(inventory).flat();
        const totalIngredients = allItems.length;

        const lowStockItems = allItems.filter(item => {
            const min = item.min_stock_level || 0;
            return item.current_quantity >= min && item.current_quantity <= min * 1.25 && item.current_quantity > 0;
        }).length;

        const criticalItems = allItems.filter(item => {
            const min = item.min_stock_level || 0;
            return item.current_quantity > 0 && item.current_quantity < min;
        }).length;

        const outOfStockItems = allItems.filter(item => item.current_quantity === 0).length;

        return {
            totalIngredients,
            lowStockItems,
            criticalItems,
            outOfStockItems
        };
    }, [inventory]);

    // Calculate KPI metrics for ingredient details
    const detailsKpis = useMemo(() => {
        if (!ingredients || ingredients.length === 0) {
            return { total: 0, active: 0, lowStock: 0, outOfStock: 0, packaging: 0, totalValue: 0 };
        }
        const total = ingredients.length;
        const active = ingredients.filter(i => i.is_active).length;
        const lowStock = ingredients.filter(i => getStockStatus(i) === 'low-stock').length;
        const outOfStock = ingredients.filter(i => getStockStatus(i) === 'out-of-stock').length;
        const packaging = ingredients.filter(i => i.is_packaging_material).length;
        const totalValue = ingredients.reduce((sum, i) => sum + ((i.current_stock || 0) * (i.cost_per_unit || 0)), 0);
        return { total, active, lowStock, outOfStock, packaging, totalValue };
    }, [ingredients]);

    // Calculate KPI metrics for finished goods
    const finishedGoodsStats = useMemo(() => {
        const totalProducts = new Set(finishedGoods.map(i => i.product_id)).size;
        const totalUnits = finishedGoods.reduce((sum, item) => sum + item.quantity_on_hand, 0);
        return { totalProducts, totalUnits };
    }, [finishedGoods]);

    // Filter and sort inventory based on search and filters
    const filteredInventory = useMemo(() => {
        let filtered = { ...inventory };

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = Object.entries(filtered).reduce((acc, [category, items]) => {
                const filteredItems = items.filter(item =>
                    item.ingredient_name?.toLowerCase().includes(query)
                );
                if (filteredItems.length > 0) {
                    acc[category] = filteredItems;
                }
                return acc;
            }, {});
        }

        // Apply category filter
        if (categoryFilter !== 'all') {
            filtered = { [categoryFilter]: filtered[categoryFilter] || [] };
        }

        // Apply stock filter
        if (stockFilter !== 'all') {
            filtered = Object.entries(filtered).reduce((acc, [category, items]) => {
                let filteredItems = items;
                if (stockFilter === 'in') {
                    filteredItems = items.filter(item => {
                        const min = item.min_stock_level || 0;
                        return item.current_quantity > min * 1.25;
                    });
                } else if (stockFilter === 'low') {
                    filteredItems = items.filter(item => {
                        const min = item.min_stock_level || 0;
                        return item.current_quantity >= min && item.current_quantity <= min * 1.25 && item.current_quantity > 0;
                    });
                } else if (stockFilter === 'critical') {
                    filteredItems = items.filter(item => {
                        const min = item.min_stock_level || 0;
                        return item.current_quantity > 0 && item.current_quantity < min;
                    });
                } else if (stockFilter === 'out') {
                    filteredItems = items.filter(item => item.current_quantity === 0);
                }
                if (filteredItems.length > 0) {
                    acc[category] = filteredItems;
                }
                return acc;
            }, {});
        }

        // Apply sorting
        if (sortBy === 'name') {
            filtered = Object.entries(filtered).reduce((acc, [category, items]) => {
                acc[category] = [...items].sort((a, b) =>
                    (a.ingredient_name || '').localeCompare(b.ingredient_name || '')
                );
                return acc;
            }, {});
        } else if (sortBy === 'quantity') {
            filtered = Object.entries(filtered).reduce((acc, [category, items]) => {
                acc[category] = [...items].sort((a, b) =>
                    b.current_quantity - a.current_quantity
                );
                return acc;
            }, {});
        }

        return filtered;
    }, [inventory, searchQuery, categoryFilter, stockFilter, sortBy]);

    // Get available categories for filter dropdown
    const availableCategories = useMemo(() => {
        return Object.keys(inventory).sort();
    }, [inventory]);

    // Render skeleton loader for levels view
    const renderSkeletonLoader = () => {
        return (
            <>
                {/* Skeleton KPI Cards */}
                <div className="kpi-summary-strip">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="kpi-card skeleton">
                            <div className="skeleton-icon"></div>
                            <div className="skeleton-content">
                                <div className="skeleton-value"></div>
                                <div className="skeleton-label"></div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Skeleton Search Bar */}
                <div className="search-filter-bar">
                    <div className="skeleton-search"></div>
                    <div className="skeleton-filter"></div>
                    <div className="skeleton-filter"></div>
                    <div className="skeleton-filter"></div>
                </div>

                {/* Skeleton Grid */}
                <div className="inventory-grid">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="skeleton-category">
                            <div className="skeleton-category-header"></div>
                            <div className="skeleton-category-items">
                                {[1, 2, 3].map(j => (
                                    <div key={j} className="skeleton-item"></div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const renderStatsRow = () => {
        if (activeTab === 'levels') {
            return (
                <div className="kpi-summary-strip">
                    <div className="kpi-card total">
                        <div className="kpi-icon-circle total">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <rect x="3" y="3" width="7" height="9" /><rect x="14" y="3" width="7" height="5" /><rect x="14" y="12" width="7" height="9" /><rect x="3" y="16" width="7" height="5" />
                            </svg>
                        </div>
                        <div className="kpi-card-content">
                            <span className="kpi-value">{kpiMetrics.totalIngredients}</span>
                            <span className="kpi-label">TOTAL ITEMS</span>
                            <span className="kpi-desc">All inventory items</span>
                        </div>
                    </div>

                    <div className="kpi-card warning">
                        <div className="kpi-icon-circle warning">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                            </svg>
                        </div>
                        <div className="kpi-card-content">
                            <span className="kpi-value">{kpiMetrics.lowStockItems}</span>
                            <span className="kpi-label">LOW STOCK</span>
                            <span className="kpi-desc">Items running low</span>
                        </div>
                    </div>

                    <div className="kpi-card danger-critical">
                        <div className="kpi-icon-circle danger-critical">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <div className="kpi-card-content">
                            <span className="kpi-value">{kpiMetrics.criticalItems}</span>
                            <span className="kpi-label">CRITICAL STOCK</span>
                            <span className="kpi-desc">Below minimum level</span>
                        </div>
                    </div>

                    <div className="kpi-card danger-out">
                        <div className="kpi-icon-circle danger-out">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                        </div>
                        <div className="kpi-card-content">
                            <span className="kpi-value">{kpiMetrics.outOfStockItems}</span>
                            <span className="kpi-label">OUT OF STOCK</span>
                            <span className="kpi-desc">Items out of stock</span>
                        </div>
                    </div>
                </div>
            );
        }
        if (activeTab === 'details') {
            return (
                <div className="kpi-cards-section">
                    <div className="kpi-card">
                        <div className="kpi-icon default">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 7h-9M14 17H5M17 14V3M7 14v9"/>
                            </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">{detailsKpis.total}</div>
                            <div className="kpi-label">Total Ingredients</div>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-icon success">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">{detailsKpis.active}</div>
                            <div className="kpi-label">Active Items</div>
                        </div>
                    </div>

                    <div className="kpi-card warning">
                        <div className="kpi-icon warning">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">{detailsKpis.lowStock}</div>
                            <div className="kpi-label">Low Stock Items</div>
                        </div>
                    </div>

                    <div className="kpi-card danger">
                        <div className="kpi-icon danger">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10"/>
                                <line x1="15" y1="9" x2="9" y2="15"/>
                                <line x1="9" y1="9" x2="15" y2="15"/>
                            </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">{detailsKpis.outOfStock}</div>
                            <div className="kpi-label">Out of Stock</div>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-icon info">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="1" x2="12" y2="23"/>
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                            </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">${detailsKpis.totalValue.toFixed(0)}</div>
                            <div className="kpi-label">Total Value</div>
                        </div>
                    </div>

                    <div className="kpi-card">
                        <div className="kpi-icon packaging">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                                <line x1="12" y1="22.08" x2="12" y2="12"/>
                            </svg>
                        </div>
                        <div className="kpi-content">
                            <div className="kpi-value">{detailsKpis.packaging}</div>
                            <div className="kpi-label">Packaging Materials</div>
                        </div>
                    </div>
                </div>
            );
        }
        if (activeTab === 'finished-goods') {
            return (
                <div className="fg-metrics-grid">
                    <div className="fg-metric-card">
                        <div className="fg-metric-icon-wrap icon-products">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                            </svg>
                        </div>
                        <div className="fg-metric-data">
                            <span className="fg-metric-number">{finishedGoodsStats.totalProducts}</span>
                            <span className="fg-metric-label">PRODUCTS</span>
                        </div>
                    </div>

                    <div className="fg-metric-card">
                        <div className="fg-metric-icon-wrap icon-formats">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M21 12H3" />
                                <path d="M12 3v18" />
                            </svg>
                        </div>
                        <div className="fg-metric-data">
                            <span className="fg-metric-number">{finishedGoods.length}</span>
                            <span className="fg-metric-label">FORMAT LINES</span>
                        </div>
                    </div>

                    <div className="fg-metric-card highlight">
                        <div className="fg-metric-icon-wrap icon-units">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <ellipse cx="12" cy="5" rx="9" ry="3" />
                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                                <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
                            </svg>
                        </div>
                        <div className="fg-metric-data">
                            {/* Sums quantity_on_hand across mixed output units (cans, kegs,
                                bottles, gallons) — not a single real unit, so this is
                                formatted as a plain discrete count ('S') rather than tied
                                to any one unit's precision rule. */}
                            <span className="fg-metric-number">{formatQuantity(finishedGoodsStats.totalUnits, 'S')}</span>
                            <span className="fg-metric-label">TOTAL UNITS ON HAND</span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    // Render logic
    const renderContent = () => {
        // Finished goods tab manages its own data fetching
        if (activeTab === 'finished-goods') {
            return (
                <FinishedGoodsTab
                    items={finishedGoods}
                    isLoading={isFinishedGoodsLoading}
                    onRefresh={fetchFinishedGoods}
                />
            );
        }

        if (isLoading) {
            if (activeTab === 'levels') {
                return renderSkeletonLoader();
            }
            return (
                <PageLoader
                    title="Loading inventory data..."
                    message="Preparing your inventory overview"
                />
            );
        }

        if (error) {
            return (
                <div className="inventory-manager error">
                    <div className="error-message">
                        <h2>Error Loading Data</h2>
                        <p>{error}</p>
                        <button className="btn btn-primary" onClick={activeTab === 'levels' ? fetchInventory : fetchIngredients}>
                            Try Again
                        </button>
                    </div>
                </div>
            );
        }

        if (activeTab === 'details') {
            return (
                <IngredientList
                    ingredients={ingredients}
                    isLoading={isLoading}
                    onEdit={handleEditIngredient}
                    onDelete={handleDeleteIngredient}
                />
            );
        }

        // Levels View
        return (
            <>
                {/* Search and Filter Controls */}
                <div className="search-filter-bar">
                    <div className="search-box">
                        <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.35-4.35" />
                        </svg>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Search ingredient..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="clear-btn" onClick={() => setSearchQuery('')}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="filter-controls">
                        <select
                            className="filter-select"
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                        >
                            <option value="all">All Categories</option>
                            {availableCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>

                        <select
                            className="filter-select"
                            value={stockFilter}
                            onChange={(e) => setStockFilter(e.target.value)}
                        >
                            <option value="all">All Stock</option>
                            <option value="in">In Stock</option>
                            <option value="low">Low Stock</option>
                            <option value="critical">Critical Stock</option>
                            <option value="out">Out of Stock</option>
                        </select>

                        <select
                            className="filter-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <option value="name">Name (A-Z)</option>
                            <option value="quantity">Quantity (High-Low)</option>
                        </select>

                        <button
                            className="btn btn-secondary reset-filters-btn"
                            onClick={() => {
                                setSearchQuery('');
                                setCategoryFilter('all');
                                setStockFilter('all');
                                setSortBy('name');
                            }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M16 3h5v5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 21H3v-5" /></svg>
                            Reset
                        </button>
                    </div>
                </div>

                {/* Sticky Categories & Status Filter Toolbar */}
                <div className={`gsp-sticky-filter-toolbar ${isSticky ? 'gsp-is-sticky' : ''}`}>
                    <div className="gsp-sticky-toolbar-row">
                        <span className="gsp-row-label">Categories</span>
                        <div className="gsp-chips-scroll-container">
                            {ALL_STICKY_CATEGORIES.map(cat => (
                                <button
                                    key={cat.id}
                                    className={`gsp-category-chip ${categoryFilter === cat.id ? 'active' : ''}`}
                                    onClick={() => handleCategoryChipClick(cat.id)}
                                >
                                    {cat.icon === 'all' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></svg>}
                                    {cat.icon === 'coffee' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 8h1a4 4 0 1 1 0 8h-1" /><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z" /><line x1="6" y1="2" x2="6" y2="4" /><line x1="10" y1="2" x2="10" y2="4" /><line x1="14" y1="2" x2="14" y2="4" /></svg>}
                                    {cat.icon === 'tea' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M2 22c1.25-3.87 3.96-7 8-7h12v-2c0-5.52-4.48-10-10-10C6.48 3 2 7.48 2 13v9Z" /><path d="M12 15v7" /></svg>}
                                    {cat.icon === 'juice' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 22a7 7 0 0 0 7-7c0-4.3-7-11-7-11S5 10.7 5 15a7 7 0 0 0 7 7Z" /></svg>}
                                    {cat.icon === 'botanicals' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2a15 15 0 0 0-10 10c0 5.5 4.5 10 10 10s10-4.5 10-10A15 15 0 0 0 12 2Z" /><path d="M12 2v20" /></svg>}
                                    {cat.icon === 'sweeteners' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L2 7l10 5 10-5-10-5Z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></svg>}
                                    {cat.icon === 'packaging' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="M3.27 6.96L12 12.01l8.73-5.05" /><path d="M12 22.08V12" /></svg>}
                                    {cat.icon === 'others' && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>}
                                    <span>{cat.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="gsp-sticky-toolbar-row">
                        <span className="gsp-row-label">Status</span>
                        <div className="gsp-chips-scroll-container">
                            <button
                                className={`gsp-status-chip status-all ${stockFilter === 'all' ? 'active' : ''}`}
                                onClick={() => setStockFilter('all')}
                            >
                                All
                            </button>
                            <button
                                className={`gsp-status-chip status-in ${stockFilter === 'in' ? 'active' : ''}`}
                                onClick={() => setStockFilter('in')}
                            >
                                <span className="status-dot green"></span>
                                In Stock
                            </button>
                            <button
                                className={`gsp-status-chip status-low ${stockFilter === 'low' ? 'active' : ''}`}
                                onClick={() => setStockFilter('low')}
                            >
                                <span className="status-dot yellow"></span>
                                Low Stock
                            </button>
                            <button
                                className={`gsp-status-chip status-critical ${stockFilter === 'critical' ? 'active' : ''}`}
                                onClick={() => setStockFilter('critical')}
                            >
                                <span className="status-dot orange"></span>
                                Critical
                            </button>
                            <button
                                className={`gsp-status-chip status-out ${stockFilter === 'out' ? 'active' : ''}`}
                                onClick={() => setStockFilter('out')}
                            >
                                <span className="status-dot red"></span>
                                Out of Stock
                            </button>
                        </div>
                    </div>
                </div>

                {/* Inventory Grid & Empty States */}
                {Object.keys(filteredInventory).length === 0 ? (
                    <div className="gsp-empty-state-container">
                        <div className="gsp-empty-state-icon">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                            </svg>
                        </div>
                        <h3>No {stockFilter === 'low' ? 'Low Stock' : stockFilter === 'critical' ? 'Critical' : stockFilter === 'out' ? 'Out of Stock' : stockFilter === 'in' ? 'In Stock' : 'matching'} items found</h3>
                        <p>Everything looks healthy. No action required.</p>
                        <button className="btn btn-secondary" onClick={() => {
                            setSearchQuery('');
                            setCategoryFilter('all');
                            setStockFilter('all');
                        }}>
                            Reset Filters
                        </button>
                    </div>
                ) : (
                    <div className="inventory-grid">
                        {['Coffee', 'Tea', 'Juice Concentrates', 'Botanicals', 'Sweeteners', 'Packaging', 'Others'].map(categoryName => {
                            if (!filteredInventory[categoryName] || filteredInventory[categoryName].length === 0) return null;
                            return (
                                <div
                                    key={categoryName}
                                    id={`category-section-${categoryName.replace(/\s+/g, '-')}`}
                                    className="inventory-category-wrapper"
                                >
                                    <InventoryCategory
                                        categoryName={categoryName}
                                        items={filteredInventory[categoryName]}
                                        itemCount={filteredInventory[categoryName].length}
                                        onQuantityChange={handleQuantityChange}
                                        onUnitChange={handleUnitChange}
                                        onNeededChange={handleNeededChange}
                                        isAdmin={isAdmin}
                                    />
                                </div>
                            );
                        })}
                        {Object.entries(filteredInventory)
                            .filter(([categoryName]) =>
                                !['Coffee', 'Tea', 'Juice Concentrates', 'Botanicals', 'Sweeteners', 'Packaging', 'Others'].includes(categoryName)
                            )
                            .map(([categoryName, items]) => {
                                if (items.length === 0) return null;
                                return (
                                    <div
                                        key={categoryName}
                                        id={`category-section-${categoryName.replace(/\s+/g, '-')}`}
                                        className="inventory-category-wrapper"
                                    >
                                        <InventoryCategory
                                            categoryName={categoryName}
                                            items={items}
                                            itemCount={items.length}
                                            onQuantityChange={handleQuantityChange}
                                            onUnitChange={handleUnitChange}
                                            onNeededChange={handleNeededChange}
                                            isAdmin={isAdmin}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="inventory-manager">
            <PageHeader
                title="Inventory Management"
                breadcrumbCurrent="Inventory Overview"
                statsRow={renderStatsRow()}
                actions={
                    <div className="inventory-header-controls">
                        <div className="view-tabs">
                            <button
                                className={`tab-btn ${activeTab === 'levels' ? 'active' : ''}`}
                                onClick={() => {
                                    if (hasChanges()) {
                                        if (!window.confirm("You have unsaved changes. Discard them?")) return;
                                    }
                                    setActiveTab('levels');
                                }}
                            >
                                Stock Levels
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
                                onClick={() => {
                                    if (hasChanges()) {
                                        if (!window.confirm("You have unsaved changes. Discard them?")) return;
                                    }
                                    setActiveTab('details');
                                }}
                            >
                                Ingredient Details
                            </button>
                            <button
                                className={`tab-btn ${activeTab === 'finished-goods' ? 'active' : ''}`}
                                onClick={() => {
                                    if (hasChanges()) {
                                        if (!window.confirm("You have unsaved changes. Discard them?")) return;
                                    }
                                    setActiveTab('finished-goods');
                                }}
                            >
                                Finished Goods
                            </button>
                        </div>

                        <div className="header-actions">
                            {activeTab !== 'finished-goods' && (
                                <button
                                    className="btn btn-primary add-ingredient-btn"
                                    onClick={() => {
                                        setSelectedIngredient(null);
                                        setIsAddIngredientModalOpen(true);
                                    }}
                                >
                                    + Add Ingredient
                                </button>
                            )}
                            {activeTab === 'levels' && hasChanges() && (
                                <>
                                    <button
                                        className="btn btn-secondary"
                                        onClick={handleReset}
                                        disabled={isSaving}
                                    >
                                        Discard Changes
                                    </button>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                }
            />

            <div className="inventory-content-body">
                {renderContent()}
            </div>

            {/* Add/Edit Ingredient Modal */}
            <AddIngredientModal
                isOpen={isAddIngredientModalOpen}
                onClose={handleModalClose}
                onIngredientCreated={handleIngredientSaved}
                ingredient={selectedIngredient}
            />
        </div>
    );
};

export default InventoryManager;
